import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { collection, onSnapshot } from "firebase/firestore";

import {
  FaProjectDiagram,
  FaImages,
  FaTools,
  FaBlog,
  FaEye,
  FaPlus,
  FaArrowUp,
  FaExternalLinkAlt,
} from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    projects: 0,
    services: 0,
    gallery: 0,
    blogs: 0,
  });

  const [recentProjects, setRecentProjects] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  /* =====================================================
     REAL-TIME FIREBASE LISTENERS
  ===================================================== */

  useEffect(() => {
    setLoading(true);
    setError("");

    let projectsData = [];
    // let servicesCount = 0;
    let blogsCount = 0;

    /* =====================================================
       PROJECTS LISTENER
    ===================================================== */

    const unsubscribeProjects = onSnapshot(
      collection(db, "projects"),
      (snapshot) => {
        projectsData = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));

        /* ================================================
           GALLERY IMAGE COUNT
        ================================================ */

        let galleryCount = 0;

        projectsData.forEach((project) => {
          if (Array.isArray(project.galleryImages)) {
            galleryCount += project.galleryImages.length;
          }
        });

        /* ================================================
           SORT PROJECTS
        ================================================ */

        const sortedProjects = [...projectsData].sort((a, b) => {
          const getTime = (project) => {
            if (!project.createdAt) {
              return 0;
            }

            if (typeof project.createdAt.toMillis === "function") {
              return project.createdAt.toMillis();
            }

            if (typeof project.createdAt.seconds === "number") {
              return project.createdAt.seconds * 1000;
            }

            if (project.createdAt instanceof Date) {
              return project.createdAt.getTime();
            }

            const parsedDate = new Date(project.createdAt).getTime();

            return Number.isNaN(parsedDate) ? 0 : parsedDate;
          };

          return getTime(b) - getTime(a);
        });

        /* ================================================
           RECENT PROJECTS
        ================================================ */

        setRecentProjects(sortedProjects.slice(0, 5));

        /* ================================================
           UPDATE PROJECT + GALLERY COUNTERS
        ================================================ */

        setStats((previous) => ({
          ...previous,

          projects: projectsData.length,

          gallery: galleryCount,
        }));

        setLoading(false);
      },
      (err) => {
        console.error("Projects listener error:", err);

        setError(err.message || "Unable to load projects.");

        setLoading(false);
      },
    );

    /* =====================================================
       SERVICES LISTENER
    ===================================================== */

    // const unsubscribeServices = onSnapshot(
    //   collection(db, "services"),
    //   (snapshot) => {
    //     servicesCount = snapshot.size;

    //     setStats((previous) => ({
    //       ...previous,

    //       services: servicesCount,
    //     }));
    //   },
    //   (err) => {
    //     console.error("Services listener error:", err);
    //   },
    // );

    /* =====================================================
       BLOGS LISTENER
    ===================================================== */

    const unsubscribeBlogs = onSnapshot(
      collection(db, "blogs"),
      (snapshot) => {
        blogsCount = snapshot.size;

        setStats((previous) => ({
          ...previous,

          blogs: blogsCount,
        }));
      },
      (err) => {
        console.error("Blogs listener error:", err);
      },
    );

    /* =====================================================
       CLEANUP LISTENERS
    ===================================================== */

    return () => {
      unsubscribeProjects();
      // unsubscribeServices();
      unsubscribeBlogs();
    };
  }, []);

  /* =====================================================
     VIEW WEBSITE
  ===================================================== */

  const handleViewWebsite = () => {
    window.open("/", "_blank");
  };

  /* =====================================================
     LOADING
  ===================================================== */

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-spinner"></div>

        <p>Loading dashboard...</p>
      </div>
    );
  }

  /* =====================================================
     ERROR
  ===================================================== */

  if (error) {
    return (
      <div className="dashboard-error">
        <FaProjectDiagram />

        <h3>Dashboard Error</h3>

        <p>{error}</p>
      </div>
    );
  }

  /* =====================================================
     PAGE
  ===================================================== */

  return (
    <div className="dashboard">
      {/* =================================================
          HEADER
      ================================================= */}

      <div className="dashboard-header">
        <div>
          <h2>Dashboard</h2>

          <p>Welcome back to Quality Sports Surface Admin Panel.</p>
        </div>

        <button className="view-website-btn" onClick={handleViewWebsite}>
          <FaEye />
          View Website
          <FaExternalLinkAlt />
        </button>
      </div>

      {/* =================================================
          STATISTICS
      ================================================= */}

      <div className="dashboard-stats">
        {/* PROJECTS */}

        <div className="stat-card">
          <div className="stat-icon projects-icon">
            <FaProjectDiagram />
          </div>

          <div className="stat-info">
            <span>Total Projects</span>

            <h3>{stats.projects}</h3>

            <small>
              <FaArrowUp />
              Live
            </small>
          </div>
        </div>

        {/* SERVICES */}

        {/* <div className="stat-card">
          <div className="stat-icon services-icon">
            <FaTools />
          </div>

          <div className="stat-info">
            <span>Total Services</span>

            <h3>{stats.services}</h3>

            <small>
              <FaArrowUp />
              Live
            </small>
          </div>
        </div> */}

        {/* GALLERY */}

        <div className="stat-card">
          <div className="stat-icon gallery-icon">
            <FaImages />
          </div>

          <div className="stat-info">
            <span>Gallery Images</span>

            <h3>{stats.gallery}</h3>

            <small>
              <FaArrowUp />
              Live
            </small>
          </div>
        </div>

        {/* BLOGS */}

        <div className="stat-card">
          <div className="stat-icon blogs-icon">
            <FaBlog />
          </div>

          <div className="stat-info">
            <span>Total Blogs</span>

            <h3>{stats.blogs}</h3>

            <small>
              <FaArrowUp />
              Live
            </small>
          </div>
        </div>
      </div>

      {/* =================================================
          RECENT PROJECTS
      ================================================= */}

      <div className="dashboard-section">
        <div className="section-header">
          <div>
            <h3>Recent Projects</h3>

            <p>Your latest completed projects.</p>
          </div>

          <button onClick={() => navigate("/admin/projects")}>View All</button>
        </div>

        {recentProjects.length > 0 ? (
          <div className="recent-projects">
            {recentProjects.map((project) => (
              <div
                className="recent-project"
                key={project.id}
                onClick={() => navigate(`/admin/projects/${project.id}`)}
              >
                <div className="recent-project-image">
                  {project.image ||
                  project.coverImage ||
                  project.galleryImages?.[0] ? (
                    <img
                      src={
                        project.image ||
                        project.coverImage ||
                        project.galleryImages?.[0]
                      }
                      alt={project.title || "Project"}
                    />
                  ) : (
                    <FaProjectDiagram />
                  )}
                </div>

                <div className="recent-project-info">
                  <h4>{project.title || "Untitled Project"}</h4>

                  <span>{project.sport || "Sports Project"}</span>

                  <small>{project.location || "Location not specified"}</small>
                </div>

                <FaExternalLinkAlt />
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaProjectDiagram />
            </div>

            <h3>No Projects Yet</h3>

            <p>Once you add projects, your latest projects will appear here.</p>

            <button
              className="add-project-btn"
              onClick={() => navigate("/admin/projects/add")}
            >
              <FaPlus />
              Add Your First Project
            </button>
          </div>
        )}
      </div>

      {/* =================================================
          QUICK ACTIONS
      ================================================= */}

      <div className="dashboard-section">
        <div className="section-header">
          <div>
            <h3>Quick Actions</h3>

            <p>Manage your website content quickly.</p>
          </div>
        </div>

        <div className="quick-actions">
          {/* PROJECT */}

          <button onClick={() => navigate("/admin/projects/add")}>
            <FaProjectDiagram />

            <span>
              <strong>Add Project</strong>

              <small>Add a completed sports project</small>
            </span>
          </button>

          {/* SERVICES */}

          <button onClick={() => navigate("/admin/services")}>
            <FaTools />

            <span>
              <strong>Manage Services</strong>

              <small>Add or update sports services</small>
            </span>
          </button>

          {/* GALLERY */}

          <button onClick={() => navigate("/admin/gallery")}>
            <FaImages />

            <span>
              <strong>Upload Gallery</strong>

              <small>Add project photos</small>
            </span>
          </button>

          {/* BLOG */}

          <button onClick={() => navigate("/admin/blogs")}>
            <FaBlog />

            <span>
              <strong>Create Blog</strong>

              <small>Publish a new article</small>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
